import { useCallback, useState, useRef } from 'react';
import { useDeleteDialog } from '../..';
import { GridPaginationModel } from '@mui/x-data-grid';
import { apiDelete, apiGet, apiPost, showErrorToast } from '../../../services/apiClient';
import { toast } from '../../../utils/toast';
import { useTranslation } from 'react-i18next';
import StandardDialog from '../../../components/dialogs/base/standardDialog';
import { Reservation } from '../../../models/reservation';
import Form, { ReservationFormData, FormRef } from '../../../pages/reservations/components/form';
import { Driver } from '../../../models/driver';
import { Vehicle } from '../../../models/vehicle';

const useReservations = (options?: { lockedDriver?: Driver; lockedVehicle?: Vehicle; onSuccess?: () => void }) => {
  const { t } = useTranslation();

  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [query, setQuery] = useState({});
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState<ReservationFormData>({});
  const [vehicleId, setVehicleId] = useState<number | null>(null);
  const [action, setAction] = useState<'view' | 'create' | 'update'>('create');
  const { DeleteModal, confirmDelete } = useDeleteDialog();
  const [rowCount, setRowCount] = useState(0);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 15
  });
  const formRef = useRef<FormRef>(null);

  const fetchReservations = useCallback(
    async () => {
      try {
        setLoading(true);
        const { page, pageSize } = paginationModel;
        const resp = await apiGet(`${process.env.REACT_APP_API_URL}/api/v1/reservations`, {
          ...query,
          offset: page * pageSize,
          limit: pageSize,
        });
        const { result, count } = await resp.json();

        const results = result.map((reservation: Reservation) => ({
          ...reservation,
          startTime: reservation.startTime,
          endTime: reservation.endTime,
          pickupCountry: reservation.pickupLocation?.country,
          pickupCity: reservation.pickupLocation?.city,
          pickupPostalCode: reservation.pickupLocation?.postalCode,
          dropoffCountry: reservation.dropoffLocation?.country,
          dropoffCity: reservation.dropoffLocation?.city,
          dropoffPostalCode: reservation.dropoffLocation?.postalCode,
        }));

        setReservations(results);
        setRowCount(count);
      } catch (error) {
        showErrorToast(error, t('Failed to fetch reservations'));
      } finally {
        setLoading(false);
      }
    },
    [query, paginationModel]
  );

  const payload = {
    driver_id: formData.driver?.id,
    vehicle_id: formData.vehicleId,
    start_time: formData.startTime ? new Date(formData.startTime).getTime() : '',
    end_time: formData.endTime ? new Date(formData.endTime).getTime() : '',
    pickup_location: {
      country: formData.pickupCountry,
      city: formData.pickupCity,
      postal_code: formData.pickupPostalCode
    },
    dropoff_location: {
      country: formData.dropoffCountry,
      city: formData.dropoffCity,
      postal_code: formData.dropoffPostalCode
    },
    no_driver_reason: formData.noDriverReason,
  };

  const handleSubmit = async () => {
    const vid = formData.vehicleId ?? formData.vehicle?.id ?? vehicleId;
    if (!vid) {
      toast.error(t('Please select a vehicle'));
      return;
    }
    try {
      setSubmitting(true);
      const url = `${process.env.REACT_APP_API_URL}/api/v1/vehicle/${vid}/reservations/create`;
      await apiPost(url, payload);
      toast.success(`${t('Reservation')} ${t('created')}!`);
      setFormData({});
      setVehicleId(null);
      setIsOpen(false);
      await fetchReservations();
      options?.onSuccess?.();
    } catch (error) {
      showErrorToast(error, t('Something went wrong!'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string | number) => {
    const reservation = reservations.find(item => item.id === id);
    // Pass the reservation identifier, not the question (dialog already has the question)
    const reservationLabel = reservation?.id ? `Reservation #${reservation.id}` : t('this reservation');
    const ok = await confirmDelete(reservationLabel);
    if (!ok) return;
    try {
      await apiDelete(`${process.env.REACT_APP_API_URL}/api/v1/vehicle/${reservation?.vehicle_id}/reservation/${id}`);
      toast.success(t('Successfully deleted!'));
      await fetchReservations();
    } catch (error) {
      showErrorToast(error, t('Delete failed!'));
    }
  };

  const Dialog =
        <StandardDialog
          open={isOpen}
          onClose={() => { setIsOpen(false); setFormData({}); setVehicleId(null); setAction('create'); }}
          title={action === 'create' ? t('Add Reservation') : t('View reservation')}
          maxWidth="md"
          width={{ xs: '100%', sm: '90%', md: '700px', lg: '750px' }}
          height={{ xs: '100vh', sm: '95vh', md: '90vh', lg: '85vh' }}
          primaryAction={action === 'create' ? {
            label: submitting ? t('Creating...') : t('Add Reservation'),
            onClick: async () => {
              if (formRef.current) {
                await formRef.current.submit();
              }
            },
            loading: submitting,
            disabled: submitting
          } : undefined}
          secondaryAction={{
            label: t('Cancel'),
            onClick: () => { setIsOpen(false); setFormData({}); setVehicleId(null); setAction('create'); }
          }}
          contentSx={{ paddingX: { xs: 1.5, sm: 2, md: 2 } }}
        >
          <Form
            ref={formRef}
            formData={formData}
            setFormData={setFormData}
            handleSubmit={handleSubmit}
            actionText={<></>}
            lockedDriver={options?.lockedDriver}
            lockedVehicle={options?.lockedVehicle}
          />
        </StandardDialog>;

  return {
    reservations,
    query,
    isOpen,
    loading,
    formData,
    action,
    DeleteModal,
    rowCount,
    paginationModel,
    Dialog,
    fetchReservations,
    setQuery,
    setIsOpen,
    setFormData,
    setVehicleId,
    setAction,
    handleSubmit,
    handleDelete,
    setPaginationModel,
  };
};

export default useReservations;